import type { FieldTypeCard } from './card';
import { textField } from './text-field';
import { check } from '../format/schema';
import { richText } from './rich-text';
import { phoneNumber } from './phone-number';
import { email } from './email';
import { select } from './select';
import { multiselect } from './multiselect';
import { checkbox } from './checkbox';
import { checkboxGroup } from './checkboxGroup';
import { numberStepper } from './numberStepper';
import { datepicker } from './datePicker';
import { attachment } from './attachment';
import { repeatableGroup } from './repeatableGroup';
import { FIELD_WIDTH, type FieldStyles, type FieldWidth } from '../format/fieldWidth';

interface FieldLayout {
  type: string;
  settings?: object;
  styles?: FieldStyles;
}

export const fieldTypes = [
  textField,
  richText,
  email,
  phoneNumber,
  select,
  multiselect,
  checkboxGroup,
  checkbox,
  numberStepper,
  datepicker,
  attachment,
  repeatableGroup,
];

const byType: Map<string, FieldTypeCard> = new Map(fieldTypes.map(card => [card.type, card]));

export const fieldTypeOf = (field: { type: string }): FieldTypeCard => {
  const card = byType.get(field.type);
  if (!card) throw new Error(`Unknown Field Type “${field.type}”`);
  return card;
};

export const settingsOf = (field: { type: string; settings?: object }): Record<string, unknown> => ({
  ...fieldTypeOf(field).defaults,
  ...field.settings,
});

export const canResizeField = (field: FieldLayout): boolean =>
  fieldTypeOf(field).canResize?.(settingsOf(field)) ?? true;

export const defaultFieldWidthOf = (field: FieldLayout): FieldWidth =>
  fieldTypeOf(field).defaultWidth?.(settingsOf(field)) ?? FIELD_WIDTH.THIRD;

export const fieldWidthOf = (field: FieldLayout): FieldWidth =>
  canResizeField(field) ? (field.styles?.width ?? defaultFieldWidthOf(field)) : FIELD_WIDTH.FULL;

export const fitsFieldType = (field: { type: string }, value: unknown): boolean => {
  return check(fieldTypeOf(field).value, value).ok;
};
